var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { decodeFixedPointType, isCurrencyEqual, newMonetaryAmount, newVaultCurrencyPair, newVaultId, queryNominationsMap, storageKeyToNthInner, } from "../utils";
import { NominationStatus } from "../types";
import { currencyIdToMonetaryCurrency } from "../utils/currency";
export var NominationAmountType;
(function (NominationAmountType) {
    NominationAmountType["Raw"] = "raw";
    NominationAmountType["Parsed"] = "parsed";
})(NominationAmountType || (NominationAmountType = {}));
export class DefaultNominationAPI {
    constructor(api, wrappedCurrency, vaultsAPI, rewardsAPI) {
        this.api = api;
        this.wrappedCurrency = wrappedCurrency;
        this.vaultsAPI = vaultsAPI;
        this.rewardsAPI = rewardsAPI;
    }
    static buildDepositCollateralExtrinsic(api, vaultAccountId, amount, wrappedCurrency) {
        const vaultId = newVaultId(api, vaultAccountId.toString(), amount.currency, wrappedCurrency);
        const amountAsPlanck = api.createType("Balance", amount.toString(true));
        return api.tx.nomination.depositCollateral(vaultId, amountAsPlanck);
    }
    depositCollateral(vaultAccountId, amount) {
        const tx = DefaultNominationAPI.buildDepositCollateralExtrinsic(this.api, vaultAccountId, amount, this.wrappedCurrency);
        return { extrinsic: tx, event: this.api.events.nomination.DepositCollateral };
    }
    static buildWithdrawCollateralExtrinsic(api, rewardsAPI, vaultAccountId, amount, wrappedCurrency, nonce) {
        return __awaiter(this, void 0, void 0, function* () {
            const vaultId = newVaultId(api, vaultAccountId.toString(), amount.currency, wrappedCurrency);
            const definedNonce = nonce ? nonce : yield rewardsAPI.getStakingPoolNonce(amount.currency, vaultAccountId);
            const amountAsPlanck = api.createType("Balance", amount.toString(true));
            const parsedNonce = api.createType("Index", definedNonce);
            return api.tx.nomination.withdrawCollateral(vaultId, amountAsPlanck, parsedNonce);
        });
    }
    withdrawCollateral(vaultAccountId, amount, nonce) {
        return __awaiter(this, void 0, void 0, function* () {
            const tx = yield DefaultNominationAPI.buildWithdrawCollateralExtrinsic(this.api, this.rewardsAPI, vaultAccountId, amount, this.wrappedCurrency, nonce);
            return { extrinsic: tx, event: this.api.events.nomination.WithdrawCollateral };
        });
    }
    optIn(collateralCurrency) {
        const vaultCurrencyPair = newVaultCurrencyPair(this.api, collateralCurrency, this.wrappedCurrency);
        const tx = this.api.tx.nomination.optInToNomination(vaultCurrencyPair);
        return { extrinsic: tx, event: this.api.events.nomination.NominationOptIn };
    }
    optOut(collateralCurrency) {
        const vaultCurrencyPair = newVaultCurrencyPair(this.api, collateralCurrency, this.wrappedCurrency);
        const tx = this.api.tx.nomination.optOutOfNomination(vaultCurrencyPair);
        return { extrinsic: tx, event: this.api.events.nomination.NominationOptOut };
    }
    setNominationEnabled(enabled) {
        const tx = this.api.tx.sudo.sudo(this.api.tx.nomination.setNominationEnabled(enabled));
        return { extrinsic: tx };
    }
    isNominationEnabled() {
        return __awaiter(this, void 0, void 0, function* () {
            const isNominationEnabled = yield this.api.query.nomination.nominationEnabled();
            return isNominationEnabled.isTrue;
        });
    }
    getNonces() {
        return __awaiter(this, void 0, void 0, function* () {
            const vaultIds = yield this.listVaults();
            const nonceMap = new Map();
            for (const vaultId of vaultIds) {
                const nonce = yield this.rewardsAPI.getStakingPoolNonce(yield currencyIdToMonetaryCurrency(this.api, vaultId.currencies.collateral), vaultId.accountId);
                nonceMap.set(vaultId, nonce);
            }
            return nonceMap;
        });
    }
    listAllNominations() {
        return __awaiter(this, void 0, void 0, function* () {
            const nonces = yield this.getNonces();
            const stakesMap = yield this.api.query.vaultStaking.stake.entries();
            const nominations = [];
            for (const v of stakesMap) {
                const nonce = storageKeyToNthInner(v[0], 0);
                const [vaultId, nominatorId] = storageKeyToNthInner(v[0], 1);
                const nomination = decodeFixedPointType(v[1]);
                const collateralCurrency = yield currencyIdToMonetaryCurrency(this.api, vaultId.currencies.collateral);
                const monetaryNomination = newMonetaryAmount(nomination, collateralCurrency, true);
                const rawNomination = {
                    nonce: nonce.toNumber(),
                    vaultId,
                    nominatorId,
                    amount: monetaryNomination,
                    type: NominationAmountType.Raw,
                };
                // Cannot just do `nonces.get(rawNomination.vaultId)` because vaultId objects differ
                // ever so slightly even if they have identical properties
                const vaultIdHighestNonce = yield queryNominationsMap(this.api, nonces, rawNomination.vaultId);
                // Only consider active nominations, i.e. with the latest nonce
                if (rawNomination.nonce === vaultIdHighestNonce && rawNomination.amount.toBig().gt(0)) {
                    nominations.push(rawNomination);
                }
            }
            return nominations;
        });
    }
    listNominatorRewards() {
        return __awaiter(this, void 0, void 0, function* () {
            const rawList = yield this.listAllNominations();
            return yield Promise.all(rawList.map((rawNomination) => __awaiter(this, void 0, void 0, function* () {
                const reward = yield this.vaultsAPI.computeReward(rawNomination.vaultId.accountId, yield currencyIdToMonetaryCurrency(this.api, rawNomination.vaultId.currencies.collateral), yield currencyIdToMonetaryCurrency(this.api, rawNomination.vaultId.currencies.wrapped));
                return {
                    nonce: rawNomination.nonce,
                    vaultId: rawNomination.vaultId,
                    nominatorId: rawNomination.nominatorId,
                    amount: reward,
                };
            })));
        });
    }
    getActiveNominatorRewards(nominatorId) {
        return __awaiter(this, void 0, void 0, function* () {
            const nominatorRewards = yield this.listNominatorRewards();
            return nominatorRewards.filter((nominationReward) => {
                return nominationReward.nominatorId.toString() === nominatorId.toString();
            });
        });
    }
    getNominatorReward(vaultId, collateralCurrency, rewardCurrency) {
        return __awaiter(this, void 0, void 0, function* () {
            return yield this.vaultsAPI.computeReward(vaultId, collateralCurrency, rewardCurrency);
        });
    }
    list() {
        return __awaiter(this, void 0, void 0, function* () {
            return yield this.getFilteredNominations();
        });
    }
    getFilteredNominations(vaultId, collateralCurrency, nominatorId) {
        return __awaiter(this, void 0, void 0, function* () {
            const rawList = yield this.listAllNominations();
            // Filter by nominatorId and vaultId if each is defined respectively.
            const rawNominations = [];
            for (const rawNomination of rawList) {
                const nominationCurrency = yield currencyIdToMonetaryCurrency(this.api, rawNomination.vaultId.currencies.collateral);
                const nominationWrappedCurrency = yield currencyIdToMonetaryCurrency(this.api, rawNomination.vaultId.currencies.wrapped);
                const wrappedCurrency = this.wrappedCurrency;
                if ((!nominatorId || nominatorId === nominatorId) &&
                    (!vaultId || vaultId === vaultId) &&
                    (!collateralCurrency || isCurrencyEqual(collateralCurrency, nominationCurrency)) &&
                    isCurrencyEqual(nominationWrappedCurrency, wrappedCurrency)) {
                    rawNominations.push(rawNomination);
                }
            }
            return yield Promise.all(rawNominations.map((rawNomination) => __awaiter(this, void 0, void 0, function* () {
                return Object.assign(Object.assign({}, rawNomination), { amount: yield this.rewardsAPI.computeCollateralInStakingPool(rawNomination.vaultId, rawNomination.nominatorId), type: NominationAmountType.Parsed });
            })));
        });
    }
    getNominationStatus(vaultId, collateralCurrency, nominatorId) {
        return __awaiter(this, void 0, void 0, function* () {
            // There is at most one entry determined by this pair
            const filteredNominations = yield this.getFilteredNominations(vaultId, collateralCurrency, nominatorId);
            if (filteredNominations.length === 0) {
                return Promise.reject(new Error("No nomination associated with this (nominator, vault) pair"));
            }
            if (filteredNominations[0].amount.isZero()) {
                return NominationStatus.Unstaked;
            }
            else {
                return NominationStatus.Staked;
            }
        });
    }
    getTotalNomination(vaultId, collateralCurrency, nominatorId) {
        return __awaiter(this, void 0, void 0, function* () {
            const filteredNominations = yield this.getFilteredNominations(vaultId, collateralCurrency, nominatorId);
            if (!filteredNominations.length) {
                // Cannot return `zero` because the type of nominated collateral is unknown
                return Promise.reject("No nomination available");
            }
            const zero = newMonetaryAmount(0, collateralCurrency);
            const ccyFilteredNominations = [];
            for (const nomination of filteredNominations) {
                const wrappedVaultCurrency = yield currencyIdToMonetaryCurrency(this.api, nomination.vaultId.currencies.wrapped);
                const collateralVaultCurrency = yield currencyIdToMonetaryCurrency(this.api, nomination.vaultId.currencies.collateral);
                if (wrappedVaultCurrency.ticker === this.wrappedCurrency.ticker &&
                    collateralCurrency &&
                    collateralVaultCurrency.ticker === collateralCurrency.ticker) {
                    ccyFilteredNominations.push(nomination);
                }
            }
            return ccyFilteredNominations
                .map((nomination) => nomination.amount)
                .reduce((previousValue, currentValue) => previousValue.add(currentValue), zero);
        });
    }
    listVaults() {
        return __awaiter(this, void 0, void 0, function* () {
            const nominatorMap = yield this.api.query.nomination.vaults.entries();
            return nominatorMap.filter((v) => v[1]).map((v) => storageKeyToNthInner(v[0]));
        });
    }
    isVaultOptedIn(accountId, collateralCurrency) {
        return __awaiter(this, void 0, void 0, function* () {
            const optedInVaults = yield this.listVaults();
            const vaultId = newVaultId(this.api, accountId.toString(), collateralCurrency, this.wrappedCurrency);
            return optedInVaults.includes(vaultId);
        });
    }
}
//# sourceMappingURL=nomination.js.map